/**
 * EL PERÍODO — el mismo control en toda la app.
 *
 * Y con **comparación contra el período anterior**, que es lo que convierte un
 * número en una noticia: «vendiste RD$21,400» no dice nada; «RD$21,400, 18 %
 * más que el mes pasado» sí.
 *
 * ═══ QUÉ ES «EL PERÍODO ANTERIOR» ═══
 *
 * El mismo largo, justo antes. Para «este mes» es el mes pasado completo; para
 * «7 días», los 7 anteriores. No se compara contra «el mismo mes del año
 * pasado»: una tienda de paca de un año para otro no se parece en nada, y esa
 * comparación diría más del calendario que del negocio.
 *
 * ⚠ Para «este mes» hay una trampa conocida: comparar 25 días corridos contra
 * un mes entero hace parecer que siempre se vende menos. Por eso el anterior se
 * recorta al MISMO número de días transcurridos. Lo dice el rótulo.
 */

export type PeriodoId = 'hoy' | 'ayer' | '7dias' | 'mes' | 'mes-pasado' | 'personalizado';

export const PERIODOS: Array<{ id: PeriodoId; etiqueta: string }> = [
  { id: 'hoy', etiqueta: 'Hoy' },
  { id: 'ayer', etiqueta: 'Ayer' },
  { id: '7dias', etiqueta: '7 días' },
  { id: 'mes', etiqueta: 'Este mes' },
  { id: 'mes-pasado', etiqueta: 'Mes pasado' },
];

export interface Rango {
  desde: Date;
  hasta: Date;
}

export interface RangoConComparacion {
  actual: Rango;
  anterior: Rango;
  /** Cómo llamar al período anterior en la pantalla. */
  etiquetaAnterior: string;
}

const DIA = 86400000;
const inicioDe = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** Convierte el período elegido en dos rangos: el actual y con qué comparar. */
export function rangoDe(
  periodo: PeriodoId,
  personalizado?: { desde?: string; hasta?: string },
  ahora = new Date(),
): RangoConComparacion {
  const hoy = inicioDe(ahora);

  switch (periodo) {
    case 'hoy': {
      const actual = { desde: hoy, hasta: new Date(hoy.getTime() + DIA) };
      return {
        actual,
        anterior: { desde: new Date(hoy.getTime() - DIA), hasta: hoy },
        etiquetaAnterior: 'ayer',
      };
    }
    case 'ayer': {
      const ayer = new Date(hoy.getTime() - DIA);
      return {
        actual: { desde: ayer, hasta: hoy },
        anterior: { desde: new Date(ayer.getTime() - DIA), hasta: ayer },
        etiquetaAnterior: 'anteayer',
      };
    }
    case '7dias': {
      const desde = new Date(hoy.getTime() - 6 * DIA);
      const hasta = new Date(hoy.getTime() + DIA);
      return {
        actual: { desde, hasta },
        anterior: { desde: new Date(desde.getTime() - 7 * DIA), hasta: desde },
        etiquetaAnterior: 'los 7 días anteriores',
      };
    }
    case 'mes-pasado': {
      const desde = new Date(ahora.getFullYear(), ahora.getMonth() - 1, 1);
      const hasta = new Date(ahora.getFullYear(), ahora.getMonth(), 1);
      return {
        actual: { desde, hasta },
        anterior: {
          desde: new Date(ahora.getFullYear(), ahora.getMonth() - 2, 1),
          hasta: desde,
        },
        etiquetaAnterior: 'el mes anterior',
      };
    }
    case 'personalizado': {
      const desde = personalizado?.desde ? new Date(personalizado.desde) : hoy;
      const hasta = personalizado?.hasta
        ? new Date(new Date(personalizado.hasta).getTime() + DIA)
        : new Date(hoy.getTime() + DIA);
      const largo = Math.max(DIA, hasta.getTime() - desde.getTime());
      return {
        actual: { desde, hasta },
        anterior: { desde: new Date(desde.getTime() - largo), hasta: desde },
        etiquetaAnterior: 'el período anterior',
      };
    }
    case 'mes':
    default: {
      const desde = new Date(ahora.getFullYear(), ahora.getMonth(), 1);
      const hasta = new Date(hoy.getTime() + DIA);
      // Los MISMOS días transcurridos del mes pasado, no el mes entero.
      const diasCorridos = Math.max(1, Math.round((hasta.getTime() - desde.getTime()) / DIA));
      const desdeAnterior = new Date(ahora.getFullYear(), ahora.getMonth() - 1, 1);
      return {
        actual: { desde, hasta },
        anterior: {
          desde: desdeAnterior,
          hasta: new Date(desdeAnterior.getTime() + diasCorridos * DIA),
        },
        etiquetaAnterior: `los primeros ${diasCorridos} días del mes pasado`,
      };
    }
  }
}

